'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  setSession,
  getRoleLabel,
  getRoleColor,
  type StaffMember,
  type StaffRole,
} from '@/lib/staffAuth';
import { getOrCreateRestaurant, fetchStaff } from '@/lib/supabaseService';
import { createClient } from '@/lib/supabase/client';

const PIN_LENGTH = 4;

const ROLE_DESTINATION: Record<StaffRole, string> = {
  cashier: '/pos-terminal',
  kitchen: '/kitchen',
  manager: '/pos-terminal',
  support: '/dashboard',
};

// Default staff used when Supabase has no staff yet (or user is not logged in)
const DEFAULT_STAFF: StaffMember[] = [
  {
    id: 'default-manager',
    name: 'Manager',
    role: 'manager',
    pin: '1234',
    avatar: 'MG',
    color: '#7C3AED',
    active: true,
  },
  {
    id: 'default-patrick',
    name: 'Patrick',
    role: 'cashier',
    pin: '6583',
    avatar: 'PA',
    color: '#0891B2',
    active: true,
  },
  {
    id: 'default-keuken',
    name: 'Keuken',
    role: 'kitchen',
    pin: '9999',
    avatar: 'KK',
    color: '#059669',
    active: true,
  },
];

export default function PinLoginClient() {
  const router = useRouter();
  const [staff, setStaff] = useState<StaffMember[]>(DEFAULT_STAFF);
  const [selected, setSelected] = useState<StaffMember | null>(null);
  const [pin, setPin] = useState('');
  const [directPin, setDirectPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return; // keep DEFAULT_STAFF
      getOrCreateRestaurant().then((restaurantId) => {
        if (!restaurantId) return;
        fetchStaff(restaurantId).then((data) => {
          const active = (data as StaffMember[]).filter((s) => s.active);
          // Only replace defaults if Supabase actually has staff records
          if (active.length > 0) {
            setStaff(active);
          }
        }).catch(() => {});
      }).catch(() => {});
    });
  }, []);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }, []);

  const handleDirectPinInput = useCallback(
    (digit: string) => {
      if (directPin.length >= PIN_LENGTH) return;
      const newPin = directPin + digit;
      setDirectPin(newPin);
      setError('');

      if (newPin.length === PIN_LENGTH) {
        setTimeout(() => {
          const match = staff.find((s) => s.pin === newPin);
          if (match) {
            setSelected(match);
            setSuccess(true);
            setSession(match);
            setTimeout(() => {
              router.push(ROLE_DESTINATION[match.role]);
            }, 600);
          } else {
            setError('Onjuiste PIN. Probeer opnieuw.');
            triggerShake();
            setDirectPin('');
          }
        }, 150);
      }
    },
    [directPin, staff, router, triggerShake]
  );

  const handleDirectBackspace = useCallback(() => {
    setDirectPin((p) => p.slice(0, -1));
    setError('');
  }, []);

  const handlePinInput = useCallback(
    (digit: string) => {
      if (pin.length >= PIN_LENGTH) return;
      const newPin = pin + digit;
      setPin(newPin);
      setError('');

      if (newPin.length === PIN_LENGTH) {
        setTimeout(() => {
          if (!selected) return;
          if (newPin === selected.pin) {
            setSuccess(true);
            setSession(selected);
            setTimeout(() => {
              router.push(ROLE_DESTINATION[selected.role]);
            }, 600);
          } else {
            setError('Onjuiste PIN. Probeer opnieuw.');
            triggerShake();
            setPin('');
          }
        }, 150);
      }
    },
    [pin, selected, router, triggerShake]
  );

  const handleBackspace = useCallback(() => {
    setPin((p) => p.slice(0, -1));
    setError('');
  }, []);

  const handleSelectStaff = useCallback((member: StaffMember) => {
    setSelected(member);
    setPin('');
    setError('');
    setSuccess(false);
  }, []);

  const handleBack = useCallback(() => {
    setSelected(null);
    setPin('');
    setError('');
    setSuccess(false);
  }, []);

  // Keyboard support
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        if (!selected) {
          handleDirectPinInput(e.key);
        } else {
          handlePinInput(e.key);
        }
      } else if (e.key === 'Backspace') {
        if (selected) handleBackspace();
        else handleDirectBackspace();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selected, handlePinInput, handleBackspace, handleDirectPinInput, handleDirectBackspace]);

  const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

  return (
    <div className="min-h-screen bg-[#0F0F14] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[30%] w-[600px] h-[600px] rounded-full bg-violet-600/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[20%] w-[400px] h-[400px] rounded-full bg-cyan-600/8 blur-[100px]" />
      </div>

      {/* Logo / Brand */}
      <div className="relative z-10 mb-10 text-center">
        <div className="inline-flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-600/30">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <span className="text-xl font-bold text-white tracking-tight">HorecaPOS</span>
        </div>
        <p className="text-sm text-white/40 font-medium">
          {selected ? 'Voer je PIN in' : 'Selecteer je profiel of typ je PIN'}
        </p>
      </div>

      {/* Staff selector */}
      {!selected && (
        <div className="relative z-10 w-full max-w-lg">
          {/* Direct PIN dots shown when typing without selecting */}
          {directPin.length > 0 && (
            <div className={`flex justify-center gap-4 mb-6 ${shake ? 'animate-shake' : ''}`}>
              {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                    i < directPin.length
                      ? 'bg-violet-400 border-violet-400 scale-110'
                      : 'bg-transparent border-white/25'
                  }`}
                />
              ))}
            </div>
          )}
          {error && directPin.length === 0 && (
            <p className="text-center text-sm text-red-400 font-500 mb-4">{error}</p>
          )}
          {error && directPin.length > 0 && (
            <p className="text-center text-sm text-red-400 font-500 mb-4">{error}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {staff.map((member) => (
              <button
                key={member.id}
                onClick={() => handleSelectStaff(member)}
                className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-white/5 border border-white/8 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg transition-transform duration-200 group-hover:scale-105"
                  style={{ backgroundColor: member.color, boxShadow: `0 8px 24px ${member.color}40` }}
                >
                  {member.avatar}
                </div>
                <div className="text-center">
                  <p className="text-sm font-600 text-white leading-tight">{member.name}</p>
                  <span className={`inline-block mt-1 text-2xs font-600 px-2 py-0.5 rounded-full ${getRoleColor(member.role)}`}>
                    {getRoleLabel(member.role)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PIN pad */}
      {selected && (
        <div className={`relative z-10 w-full max-w-xs transition-all duration-300 ${shake ? 'animate-shake' : ''}`}>
          {/* Selected staff card */}
          <div className="flex items-center gap-3 mb-8 p-4 rounded-2xl bg-white/5 border border-white/8">
            <button
              onClick={handleBack}
              className="w-8 h-8 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Terug"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/60">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ backgroundColor: selected.color }}
            >
              {selected.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-white truncate">{selected.name}</p>
              <span className={`inline-block text-2xs font-600 px-2 py-0.5 rounded-full ${getRoleColor(selected.role)}`}>
                {getRoleLabel(selected.role)}
              </span>
            </div>
          </div>

          {/* PIN dots */}
          <div className="flex justify-center gap-4 mb-8">
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                  i < pin.length
                    ? success
                      ? 'bg-emerald-400 border-emerald-400 scale-110' :'bg-violet-400 border-violet-400 scale-110' :'bg-transparent border-white/25'
                }`}
              />
            ))}
          </div>

          {/* Error */}
          {error && (
            <p className="text-center text-sm text-red-400 font-500 mb-4 -mt-4">{error}</p>
          )}

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-3">
            {PAD_KEYS.map((key, idx) => {
              if (key === '') return <div key={idx} />;
              const isBackspace = key === '⌫';
              return (
                <button
                  key={idx}
                  onClick={() => (isBackspace ? handleBackspace() : handlePinInput(key))}
                  disabled={success}
                  className={`h-16 rounded-2xl font-semibold text-lg transition-all duration-150 active:scale-95 select-none
                    ${isBackspace
                      ? 'bg-white/5 border border-white/8 text-white/50 hover:bg-white/10 hover:text-white/80' :'bg-white/8 border border-white/10 text-white hover:bg-white/15 hover:border-white/20 shadow-sm'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isBackspace ? (
                    <span className="flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                        <line x1="18" y1="9" x2="12" y2="15" />
                        <line x1="12" y1="9" x2="18" y2="15" />
                      </svg>
                    </span>
                  ) : key}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Time */}
      <ClockDisplay />

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
      `}</style>

      {/* Portal link */}
      <div className="absolute bottom-6 right-6 z-10">
        <a
          href="/portal/login"
          className="text-white/20 hover:text-white/50 text-xs transition-colors"
        >
          Partner Portaal →
        </a>
      </div>
    </div>
  );
}

function ClockDisplay() {
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }));
      setDate(now.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!time) return null;

  return (
    <div className="relative z-10 mt-12 text-center">
      <p className="text-4xl font-bold text-white/20 tabular-nums">{time}</p>
      <p className="text-sm text-white/15 mt-1 capitalize">{date}</p>
    </div>
  );
}
